/**
 * Shared constants and types used by the High CRM API and web app.
 *
 * The database is the source of truth for permissions, plans and features —
 * these lists exist for type safety and must stay in sync with
 * supabase/migrations/*_reference_data.sql (a test checks this).
 */
export const LOCALES = ['uz', 'ru', 'en'];
export const DEFAULT_LOCALE = 'uz';
export const CURRENCIES = ['UZS', 'USD', 'EUR'];
export const SCOPES = ['own', 'team', 'department', 'all'];
export const PERMISSIONS = [
    'leads.view', 'leads.create', 'leads.edit', 'leads.delete', 'leads.export', 'leads.import', 'leads.assign',
    'contacts.view', 'contacts.create', 'contacts.edit', 'contacts.delete', 'contacts.export', 'contacts.import', 'contacts.assign',
    'companies.view', 'companies.create', 'companies.edit', 'companies.delete', 'companies.export', 'companies.import', 'companies.assign',
    'deals.view', 'deals.create', 'deals.edit', 'deals.delete', 'deals.export', 'deals.import', 'deals.assign',
    'tasks.view', 'tasks.create', 'tasks.edit', 'tasks.delete', 'tasks.assign',
    'products.manage', 'pipelines.manage', 'customfields.manage', 'tags.manage',
    'revenue.view', 'reports.view', 'team_performance.view', 'dashboards.manage', 'data.export',
    'inbox.view', 'inbox.reply',
    'users.manage', 'teams.manage', 'roles.manage', 'integrations.manage', 'automations.manage',
    'billing.manage', 'settings.manage', 'audit.view', 'organization.delete',
    'ai.manage', 'ai.use', 'ai.upload_audio', 'ai.view_audio', 'ai.view_analysis', 'ai.view_coaching',
];
export const DEFAULT_ROLES = ['admin', 'manager', 'sales_rep', 'marketing', 'support', 'accountant'];
export const BOOLEAN_FEATURES = [
    'ai', 'meta_integration', 'telegram', 'google_sheets', 'whatsapp', 'advanced_analytics',
    'api_access', 'white_label', 'audit_log', 'import_export', 'custom_roles',
];
export const LIMIT_FEATURES = [
    'users', 'leads', 'pipelines', 'automations', 'integrations', 'storage_mb',
    'ai_credits_monthly', 'ai_analyses_monthly',
];
export const INDUSTRIES = [
    'education', 'real_estate', 'logistics', 'automotive', 'construction', 'retail', 'ecommerce',
    'medical', 'it', 'b2b_services', 'consulting', 'finance', 'manufacturing', 'other',
];
export const COMPANY_SIZES = ['1-5', '6-20', '21-50', '51-200', '201-500', '500+'];
export const LEAD_STATUSES = ['open', 'converted', 'lost', 'disqualified'];
export const DEAL_STATUSES = ['open', 'won', 'lost'];
export const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
export const TASK_TYPES = ['task', 'follow_up', 'call', 'meeting', 'appointment'];
export const TASK_STATUSES = ['pending', 'in_progress', 'completed', 'cancelled'];
export const SYSTEM_LEAD_SOURCES = [
    'meta', 'facebook', 'instagram', 'google', 'website', 'telegram', 'telegram_bot',
    'whatsapp', 'referral', 'manual', 'import', 'other',
];
export const CUSTOM_FIELD_ENTITIES = ['lead', 'contact', 'company', 'deal', 'product', 'task'];
export const CUSTOM_FIELD_TYPES = [
    'text', 'long_text', 'number', 'currency', 'percentage', 'date', 'datetime',
    'dropdown', 'multi_select', 'checkbox', 'radio', 'phone', 'email', 'url',
    'user', 'company', 'contact', 'file',
];
export const ACTIVITY_TYPES = [
    'lead_created', 'lead_assigned', 'lead_status_changed', 'lead_converted', 'stage_changed',
    'call', 'message', 'email', 'meeting', 'note', 'task_created', 'task_completed', 'follow_up_created',
    'deal_created', 'deal_won', 'deal_lost', 'deal_reopened', 'file_uploaded', 'ai_analysis_completed',
    'integration_event', 'other',
];
/** Activity types a user may log manually; the rest are produced by the system. */
export const MANUAL_ACTIVITY_TYPES = ['call', 'message', 'email', 'meeting', 'other'];
export const AUTOMATION_EVENTS = [
    'lead.created', 'lead.updated', 'lead.status_changed', 'lead.stage_changed', 'lead.assigned',
    'deal.created', 'deal.stage_changed', 'deal.won', 'deal.lost',
    'task.overdue', 'follow_up.due', 'form.submitted', 'integration.event', 'ai.analysis_completed',
];
export const AUTOMATION_ACTIONS = [
    'assign_user', 'assign_round_robin', 'change_status', 'change_stage', 'create_task',
    'send_notification', 'send_telegram_message', 'send_email', 'add_tag', 'update_field',
    'create_activity', 'webhook',
];
export const CONDITION_OPERATORS = [
    'eq', 'neq', 'contains', 'not_contains', 'gt', 'gte', 'lt', 'lte', 'in', 'not_in',
    'is_empty', 'is_not_empty', 'before', 'after', 'within_last_days',
];
export const RECORDING_STATUSES = ['uploaded', 'processing', 'transcribed', 'analyzing', 'completed', 'failed'];
export const AI_OPERATIONS = [
    'audio_transcription', 'conversation_analysis', 'lead_scoring', 'conversation_summary',
    'message_generation', 'follow_up_suggestion', 'sales_forecast', 'assistant_chat',
];
export const DASHBOARD_WIDGETS = [
    'total_leads', 'new_leads', 'contacted', 'qualified', 'open_deals', 'won_deals', 'lost_deals',
    'revenue', 'conversion_rate', 'average_deal_value', 'follow_ups_due', 'overdue_follow_ups',
    'leads_over_time', 'revenue_over_time', 'leads_by_source', 'sales_by_employee', 'deals_by_stage',
    'conversion_funnel', 'revenue_by_product',
];
export const DATE_RANGES = [
    'today', 'yesterday', 'last_7_days', 'last_30_days', 'this_month', 'last_month',
    'this_quarter', 'this_year', 'custom',
];
/** Machine-readable API error codes. The web app translates them (errors.<code>). */
export const ERROR_CODES = [
    'UNAUTHENTICATED', 'FORBIDDEN', 'NOT_FOUND', 'VALIDATION_FAILED', 'CONFLICT', 'DUPLICATE',
    'ORGANIZATION_REQUIRED', 'ORGANIZATION_READ_ONLY', 'PLAN_LIMIT_REACHED', 'FEATURE_NOT_AVAILABLE',
    'AI_NOT_AVAILABLE', 'AI_CREDITS_EXHAUSTED', 'AI_MONTHLY_LIMIT_REACHED', 'LAST_ADMIN',
    'INVITATION_INVALID', 'INVITATION_EMAIL_MISMATCH', 'ORGANIZATION_LIMIT_REACHED',
    'CONFIRMATION_MISMATCH', 'RATE_LIMITED', 'PROVIDER_NOT_CONFIGURED', 'INTEGRATION_ERROR',
    'INVALID_SIGNATURE', 'PAYLOAD_TOO_LARGE', 'INTERNAL',
    'INVALID_CREDENTIALS', 'ACCOUNT_LOCKED', 'EMAIL_IN_USE', 'INVALID_TOKEN', 'SIGNUP_CLOSED',
];
/** Scope rank used for comparisons (mirrors app.scope_rank in the database). */
export function scopeRank(scope) {
    switch (scope) {
        case 'all': return 4;
        case 'department': return 3;
        case 'team': return 2;
        case 'own': return 1;
        default: return 0;
    }
}
/**
 * Normalises a phone number to +<digits> (Uzbekistan-first). Mirrors
 * app.normalize_phone() in the database so imports can validate up-front.
 */
export function normalizePhone(input) {
    if (!input)
        return null;
    const trimmed = input.trim();
    const plus = trimmed.startsWith('+');
    let digits = trimmed.replace(/\D/g, '');
    if (!digits)
        return null;
    if (!plus) {
        if (digits.length === 9)
            digits = `998${digits}`;
        else if (digits.length === 10 && digits.startsWith('8'))
            digits = `998${digits.slice(1)}`;
        else if (digits.startsWith('00'))
            digits = digits.slice(2);
    }
    if (digits.length < 7 || digits.length > 15)
        return null;
    return `+${digits}`;
}
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export function isValidEmail(input) {
    return !!input && EMAIL_RE.test(input.trim());
}
//# sourceMappingURL=index.js.map